<div class="flex h-full flex-col gap-3 overflow-y-auto p-4">
  <div class="flex flex-wrap items-start gap-3">
    <div class="flex min-w-0 flex-1 flex-col gap-0.5">
      <h2 class="text-text-primary truncate text-base font-semibold">
        {{ config().name }}
      </h2>
      <p class="text-text-tertiary text-xs">
        {{ getChannelMeta(config().channel).labelKey | translate }}
        @if (messages().length > 0) {
          ·
          <span class="font-mono tabular-nums">
            {{
              'features.plan.deploy.message_count' | translate: { count: messages().length }
            }}
          </span>
        }
        @if (config().entityType) {
          · <span class="font-mono">{{ config().entityType }}</span>
        }
        @if (siteUids(); as uids) {
          ·
          <span class="font-mono tabular-nums">
            {{ 'features.plan.deploy.site_count' | translate: { count: uids.length } }}
          </span>
        }
        <!-- In the subtitle rather than above the content: a block that appears and
             disappears there would move the table it is reporting on. -->
        @if (state().refreshing) {
          ·
          <i
            data-testid="deploy-refreshing"
            [class]="ICONS.arrowsClockwise"
            class="animate-spin"></i>
        }
      </p>
    </div>

    <div class="flex shrink-0 items-center gap-2">
      @if (canReachChannelSettings()) {
        <a
          routerLink="/admin/deploy-channels"
          class="btn-cancel flex cursor-pointer items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold">
          <i [class]="ICONS.gear"></i>
          {{ 'features.plan.deploy.config_button' | translate }}
        </a>
      }
    </div>
  </div>

  <!-- Announced before the content loads: the channel is off whatever it would have sent,
       and the preview below deliberately keeps working so it can still be inspected. -->
  @if (!config().isEnabled) {
    <div
      role="status"
      class="bg-warning-soft text-warning flex items-center gap-2 rounded-sm px-3 py-2 text-xs">
      <i [class]="ICONS.prohibit"></i>
      {{ 'features.plan.deploy.channel_disabled' | translate }}
    </div>
  }

  <!-- Outside the response guard below: what the last deploy did is known from the plan's
       status alone, so a channel whose `/messages` call is still running — or failed — still
       explains the deploy the operator is asking about. -->
  <app-deploy-status-detail
    [status]="deployStatus()"
    [sending]="isSending()"
    [failedUids]="failedUids()" />

  @if (state().loading) {
    <div class="text-text-secondary flex items-center gap-2 py-6 text-xs">
      <i [class]="ICONS.arrowsClockwise" class="animate-spin text-sm"></i>
      {{ 'common.loading' | translate }}
    </div>
  }

  @if (state().error) {
    <div
      class="bg-danger-soft text-danger flex items-center gap-2 rounded-sm px-3 py-2 text-xs">
      <i [class]="ICONS.warningCircle"></i>
      {{ 'features.plan.deploy.messages_error' | translate }}
    </div>
  }

  @if (state().response; as response) {
    @if (unvalidatedSummary(); as summary) {
      <div
        role="alert"
        data-testid="deploy-unvalidated-routes-blocked"
        class="bg-danger-soft text-danger flex items-center gap-2 rounded-sm px-3 py-2 text-xs">
        <i [class]="ICONS.warningCircle"></i>
        {{
          (summary.total === 1
            ? 'features.plan.deploy.unvalidated_routes_blocked_one'
            : 'features.plan.deploy.unvalidated_routes_blocked_other'
          )
            | translate
              : {
                  count: summary.total,
                  routes:
                    summary.hidden > 0
                      ? ('features.plan.deploy.unvalidated_routes_more'
                        | translate
                          : { routes: summary.shown.join(', '), count: summary.hidden })
                      : summary.shown.join(', ')
                }
        }}
      </div>
    }

    <app-deploy-warnings-list [messages]="configWarnings()" />

    <div class="flex flex-wrap items-center gap-2">
      @if (
        config().supportsDeployInSteps &&
        response.contentKind === DeployContentKind.MESSAGES
      ) {
        <app-checkbox
          [checked]="selectedOnly()"
          [label]="
            'features.plan.deploy.selected_only' | translate: { count: selection().size }
          "
          [onChange]="(checked) => selectedOnly.set(checked)" />

        @if (pickerCandidates() !== null) {
          <app-button
            [icon]="ICONS.listChecks"
            [label]="'features.plan.deploy.select_entities' | translate"
            [type]="'cancel'"
            (clickEvent)="pickerOpen.set(true)" />
        }
      }

      @if (response.contentKind === DeployContentKind.VIEW) {
        @if (pickerCandidates() !== null) {
          <!-- Disabled with the table's checkboxes rather than instead of them: the picker
               writes the same `warehouse_uids`, so leaving it live would be the identical
               edit one click away. -->
          <app-button
            [icon]="ICONS.listChecks"
            [label]="'features.plan.deploy.select_entities' | translate"
            [type]="'cancel'"
            [disabled]="resendBlocked()"
            (clickEvent)="pickerOpen.set(true)" />
        }
        @if (viewContent()?.view) {
          <app-button
            [icon]="ICONS.eye"
            [label]="'features.plan.deploy.view_content' | translate"
            [type]="'cancel'"
            [disabled]="viewStale()"
            (clickEvent)="onViewSingle()" />
        }
      }

      <span class="ml-auto flex items-center gap-2">
        <!-- A payload channel has no locked row to explain itself, so the only place the
             refusal can be read is beside the button it disables. -->
        @if (resendBlocked()) {
          <span data-testid="deploy-resend-blocked" class="text-text-tertiary text-xs">
            {{ 'features.plan.deploy.resend_blocked' | translate }}
          </span>
        }
        <app-button
          [icon]="ICONS.paperPlaneTilt"
          [label]="
            config().supportsDeployInSteps
              ? ('features.plan.deploy.deploy_selected'
                | translate: { count: selection().size })
              : ('features.plan.deploy.deploy_all_messages' | translate)
          "
          [disabled]="!canDeploy()"
          [type]="'primary'"
          (clickEvent)="requestDeploy()" />
      </span>
    </div>

    @if (response.contentKind === DeployContentKind.MESSAGES) {
      @if (messages().length === 0) {
        <p class="text-text-secondary py-6 text-center text-xs">
          {{ 'features.plan.deploy.no_messages' | translate }}
        </p>
      } @else {
        <app-deploy-messages-table
          [messages]="messages()"
          [selectedUids]="selection()"
          [allowRedeploy]="config().allowRedeploy"
          [supportsDeployInSteps]="config().supportsDeployInSteps"
          [selectedOnly]="selectedOnly()"
          [sendingUids]="sendingUids()"
          (toggleUid)="onToggleUid($event)"
          (selectionRequested)="onSelectionRequested($event)"
          (viewMessage)="onViewMessage($event)"
          (viewHistory)="openHistory.set($event)" />
      }
    } @else {
      @if (viewSummaryContent(); as summaryContent) {
        <app-deploy-view-summary [content]="summaryContent" />
      }

      @if (siteProblemsBlockFile()) {
        <p data-testid="deploy-site-problems-block-file" class="text-danger text-xs">
          {{ 'features.plan.deploy.site_problems_block_file' | translate }}
        </p>
      }

      @if (siteUids(); as uids) {
        <app-deploy-sites-table
          [uids]="uids"
          [entityType]="config().entityType"
          [planId]="planId()"
          [selectedUids]="selection()"
          [locked]="resendBlocked()"
          [deployedUids]="deployedSiteUids()"
          [siteWarnings]="siteWarnings()"
          (toggleUid)="onSiteToggle($event)"
          (selectionRequested)="onSiteSelectionRequested($event)" />
      }
    }
  }
</div>

@if (openContent(); as open) {
  <app-deploy-content-viewer
    [title]="open.title"
    [view]="open.view"
    (closed)="openContent.set(null)"
    (copyRequested)="copyContent($event)" />
}

@if (openHistory(); as message) {
  <app-deploy-send-history-modal
    [label]="message.label"
    [sends]="message.sends"
    (closed)="openHistory.set(null)" />
}

@if (pickerOpen() && pickerCandidates() !== null) {
  <app-deploy-entity-picker
    [entityType]="config().entityType"
    [candidates]="pickerCandidates()!"
    [selectedUids]="selection()"
    [clearsFilterWhenEmpty]="sitesEmptyMeansAll()"
    (confirmed)="onPickerConfirmed($event)"
    (cancelled)="pickerOpen.set(false)" />
}

@if (confirmOpen()) {
  <app-deploy-confirm-modal
    [channelName]="config().name"
    [channel]="config().channel"
    [messageCount]="confirmCount()"
    [resendUids]="resendUids()"
    [lastSentAt]="config().supportsDeployInSteps ? null : lastSentAt()"
    (confirmed)="onConfirmed()"
    (cancelled)="onConfirmCancelled()" />
}
